// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/Recebimentos.tsx
//
//  Iteração 7 · Fase 2 — Listagem de recebimentos de compras.
//  Exibe chave NF-e, PO vinculada, valor, status e atalhos para
//  Ajuste de Preço e Manifestação DFe.
//  USA-COM-04: filtros de fornecedor, período e status no servidor (na URL).
// ════════════════════════════════════════════════════════════════════════════

import { useNavigate } from 'react-router-dom';
import {
  Plus, Tag, Link2, Calculator, Send, CheckCircle2, Loader2,
} from 'lucide-react';

import { useRecebimentos } from '@/features/compras/hooks/useRecebimento';
import type { RecebimentoListagem } from '@/features/compras/types';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BarraFiltros } from '@/components/BarraFiltros';
import { FornecedorCombobox } from '@/components/FornecedorCombobox';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';

const fmtMoeda = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtData = (s?: string) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); }
  catch { return s; }
};

const statusBadgeVariant = (s: string):
  'default' | 'secondary' | 'destructive' | 'outline' =>
  s === 'TotalmenteRecebido' ? 'default'
  : s === 'ParcialmenteRecebido' ? 'secondary'
  : s === 'Cancelado' ? 'destructive'
  : 'outline';

const STATUS_LABEL: Record<string, string> = {
  Pendente:             'Pendente',
  ParcialmenteRecebido: 'Rec. parcial',
  TotalmenteRecebido:   'Recebido',
  Cancelado:            'Cancelado',
};

const PADROES = {
  busca: '',
  status: 'todos',
  fornecedorId: 0,
  pagina: 1,
  tamanhoPagina: 20,
};

const Recebimentos = () => {
  const navigate = useNavigate();
  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });
  // Período e ordem vão ao servidor (padrão de listas da v118): a lista cresce sem limite com o tempo.
  const voltarPagina1 = () => setFiltros({ pagina: 1 });
  const periodo   = usePeriodo('30dias', voltarPagina1);
  const ordenacao = useOrdenacao<'data' | 'numero' | 'valor'>('data', true, voltarPagina1);
  const limpar = () => { limparFiltros(); periodo.setPeriodo('30dias'); };

  const { data, isLoading, isFetching } = useRecebimentos(pag.pagina, pag.tamanhoPagina, {
    busca:        filtros.busca || undefined,
    ...periodo.intervalo,
    ordenarPor:   ordenacao.ordenarPor,
    decrescente:  ordenacao.decrescente,
    fornecedorId: filtros.fornecedorId || undefined,
    status:       filtros.status === 'todos' ? undefined : filtros.status,
  });
  const itens = data?.itens ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Recebimentos de Compras</h1>
          <p className="text-muted-foreground mt-1">
            Entradas fiscais confirmadas. Use "Importar NF-e" para registrar uma nova.
          </p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/compras/recebimentos/importar')}>
          <Plus className="h-4 w-4" /> Importar NF-e
        </Button>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Chave NF-e, nº da nota, nº do recebimento ou fornecedor..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={limpar}
      >
        <div className="space-y-1 w-72">
          <Label className="text-xs text-muted-foreground">Fornecedor</Label>
          <FornecedorCombobox
            value={filtros.fornecedorId || null}
            onChange={(id) => setFiltros({ fornecedorId: id ?? 0 })}
            placeholder="Todos os fornecedores"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-40" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Recebimento</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <PeriodoFiltro estado={periodo} rotuloData="Recebimento" />
          </div>
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Grid */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : itens.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {periodo.periodo !== 'todos'
                ? 'Nenhum recebimento neste período — amplie o período para ver os anteriores.'
                : 'Nenhum recebimento encontrado.'}
              {filtrosAtivos > 0 && (
                <Button variant="link" className="ml-1 h-auto p-0" onClick={limpar}>
                  — limpar filtros
                </Button>
              )}
            </p>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel>
                    <CabecalhoOrdenavel coluna="data" ordenacao={ordenacao}>Data</CabecalhoOrdenavel>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>NF</TableHead>
                    <TableHead>Chave NF-e</TableHead>
                    <TableHead>PO</TableHead>
                    <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor</CabecalhoOrdenavel>
                    <TableHead>Itens</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.map((r: RecebimentoListagem) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.numero}</TableCell>
                      <TableCell className="text-xs">{fmtData(r.dtRecebimento)}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={r.fornecedorNome ?? ''}>
                        {r.fornecedorNome ?? '(fornecedor não encontrado)'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.numeroNf ? `${r.numeroNf}/${r.serieNf ?? '—'}` : '—'}
                      </TableCell>
                      <TableCell className="font-mono text-[10px] max-w-[140px] truncate" title={r.chaveNfe ?? ''}>
                        {r.chaveNfe ?? '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.ordemNumero ? (
                          <Badge variant="outline" className="gap-1">
                            <Link2 className="h-3 w-3" /> #{r.ordemNumero}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">Avulso</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">R$ {fmtMoeda(r.valorTotal)}</TableCell>
                      <TableCell className="text-xs">{r.quantidadeItens}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={statusBadgeVariant(r.status)} className="text-xs">
                            {STATUS_LABEL[r.status] ?? r.status}
                          </Badge>
                          {r.isThreeWayMatch && (
                            <span className="text-[10px] text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> 3-way match
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Ajustar preço"
                            onClick={() =>
                              navigate(`/compras/recebimentos/${r.id}/ajuste-preco`)
                            }
                          >
                            <Calculator className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Manifestação DFe"
                            disabled={!r.chaveNfe}
                            onClick={() =>
                              navigate(`/compras/recebimentos/${r.id}/manifestacao`)
                            }
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          {r.contaPagarGeradaId && (
                            <span
                              className="text-[10px] text-muted-foreground flex items-center gap-1"
                              title="Conta a pagar gerada por este recebimento"
                            >
                              <Tag className="h-3 w-3" /> CP gerada
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape(data)} />
    </div>
  );
};

export default Recebimentos;
